<template>
  <div class="open-card">
    <div class="open-card__totals">
      <h3 class="open-card__section-title">
        {{ $t('OPPORTUNITY_DASHBOARD.OPEN.TITLE') }}
      </h3>
      <div class="open-card__totals-row">
        <div>
          <p class="open-card__value">{{ open.count }}</p>
          <p class="open-card__label">
            {{ $t('OPPORTUNITY_DASHBOARD.OPEN.COUNT_LABEL') }}
          </p>
        </div>
        <div>
          <p class="open-card__value">{{ formattedValue }}</p>
          <p class="open-card__label">
            {{ $t('OPPORTUNITY_DASHBOARD.OPEN.VALUE_LABEL') }}
          </p>
        </div>
      </div>
    </div>

    <div class="open-card__sla">
      <div v-for="row in slaRows" :key="row.status" class="open-card__sla-row">
        <span
          class="open-card__sla-dot"
          :class="`open-card__sla-dot--${row.status}`"
        />
        <span class="open-card__sla-name">{{ row.label }}</span>
        <opportunity-dashboard-progress-bar
          :percentage="row.percentage"
          :color="row.color"
        />
        <span class="open-card__sla-count">{{ row.count }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { formatCurrencyValue } from 'dashboard/helper/opportunityDashboardHelper';
import OpportunityDashboardProgressBar from './OpportunityDashboardProgressBar.vue';

const SLA_STATUSES = ['overdue', 'warning', 'on_time'];
const SLA_COLORS = {
  overdue: 'var(--r-500)',
  warning: 'var(--y-500)',
  on_time: 'var(--g-500)',
};

export default {
  components: { OpportunityDashboardProgressBar },
  props: {
    open: {
      type: Object,
      default: () => ({ count: 0, value: 0 }),
    },
    slaBreakdown: {
      type: Object,
      default: () => ({ on_time: 0, warning: 0, overdue: 0 }),
    },
    currency: { type: String, default: 'BRL' },
  },
  computed: {
    formattedValue() {
      return formatCurrencyValue(this.open.value, this.currency);
    },
    maxSlaCount() {
      return Math.max(
        ...SLA_STATUSES.map(status => this.slaBreakdown[status] || 0),
        1
      );
    },
    slaRows() {
      return SLA_STATUSES.map(status => {
        const count = this.slaBreakdown[status] || 0;
        return {
          status,
          count,
          label: this.$t(`OPPORTUNITY_DASHBOARD.OPEN.SLA.${status}`),
          percentage: (count / this.maxSlaCount) * 100,
          color: SLA_COLORS[status],
        };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.open-card {
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-large);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-large);
  padding: var(--space-normal);
}

.open-card__totals {
  min-width: 16rem;
}

.open-card__section-title {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--space-normal);
}

.open-card__totals-row {
  display: flex;
  gap: var(--space-larger);
}

.open-card__value {
  font-size: var(--font-size-big);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.open-card__label {
  color: var(--s-500);
  font-size: var(--font-size-mini);
  margin: var(--space-micro) 0 0;
}

.open-card__sla {
  display: flex;
  flex: 1 1 22rem;
  flex-direction: column;
  gap: var(--space-small);
  justify-content: center;
  min-width: 22rem;
}

.open-card__sla-row {
  align-items: center;
  display: flex;
  gap: var(--space-small);
}

.open-card__sla-dot {
  border-radius: var(--border-radius-rounded);
  flex-shrink: 0;
  height: var(--space-small);
  width: var(--space-small);

  &--overdue {
    background: var(--r-500);
  }

  &--warning {
    background: var(--y-500);
  }

  &--on_time {
    background: var(--g-500);
  }
}

.open-card__sla-name {
  flex-shrink: 0;
  font-size: var(--font-size-small);
  white-space: nowrap;
  width: 8rem;
}

.open-card__sla-count {
  flex-shrink: 0;
  font-size: var(--font-size-small);
  text-align: right;
  width: 2rem;
}
</style>
